import { useState, useEffect, createContext, useContext } from 'react';
import {
    collection,
    addDoc,
    query,
    where,
    onSnapshot,
    serverTimestamp
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';

export interface Workspace {
    id: string;
    name: string;
    ownerId: string;
    onboardingStatus: Record<string, 'pending' | 'done' | 'pending_account'>;
    syncSettings: {
        lastFullSyncAt: any;
        frequency: '6h' | '12h' | '24h';
        currency?: string;
    };
    shopifyConfig?: {
        accessToken: string;
        shop: string;
        connectedAt: any;
    };
    metaConfig?: {
        accessToken: string;
        userId: string;
        connectedAt: any;
    };
    connectors?: {
        [key: string]: {
            platform: string;
            status: 'connected' | 'disconnected' | 'error';
            currency?: string;
            accessToken?: string;
            [key: string]: any;
        };
    };
    lastSync?: any;
}

interface WorkspaceContextType {
    workspaces: Workspace[];
    activeWorkspace: Workspace | null;
    setActiveWorkspaceId: (id: string) => void;
    createWorkspace: (name: string) => Promise<string>;
    loading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            setWorkspaces([]);
            setActiveWorkspaceId(null);
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, 'umr_workspaces'),
            where('ownerId', '==', user.uid)
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const wsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as Workspace));

            setWorkspaces(wsData);

            if (wsData.length > 0 && !activeWorkspaceId) {
                setActiveWorkspaceId(wsData[0].id);
            }

            setLoading(false);
        });

        return unsub;
    }, [user, activeWorkspaceId]);

    const activeWorkspace = workspaces.find(w => w.id === activeWorkspaceId) || null;

    const createWorkspace = async (name: string) => {
        if (!user) throw new Error('Not authenticated');

        const docRef = await addDoc(collection(db, 'umr_workspaces'), {
            name,
            ownerId: user.uid,
            createdAt: serverTimestamp(),
            onboardingStatus: {
                createBrand: 'done',
            },
            syncSettings: {
                frequency: '24h',
                lastFullSyncAt: null,
                currency: 'USD'
            }
        });

        return docRef.id;
    };

    return (
        <WorkspaceContext.Provider value={{ workspaces, activeWorkspace, setActiveWorkspaceId, createWorkspace, loading }}>
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const context = useContext(WorkspaceContext);
    if (context === undefined) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
}
